"use client";

import { ShieldAlert } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import type { Dictionary } from "@/lib/i18n/get-dictionary";

type Props = {
	sourceName: string;
	jobId: string;
	locale: string;
	t: Dictionary;
};

/**
 * ISP-block callout — informs the user the listing's source domain
 * (Telkomsel/Indihome, etc.) may be blocked, and to switch to mobile data
 * if the apply destination doesn't open. Rendered directly below the Apply
 * CTA (never above it) so the button stays the obvious next action; the
 * copy is a fallback troubleshooting tip, not a pre-emptive VPN install
 * instruction.
 *
 * Telemetry: fires `source_blocked_notice_shown` once per mount so impact
 * on apply-click rate becomes queryable (P1-6,
 * docs/plans/backlog/product-signal-audit-remediation-2026-08-04.md §11).
 * Previously shipped with zero analytics events.
 */
export function SourceBlockedNotice({ sourceName, jobId, locale, t }: Props) {
	const posthog = usePostHog();
	const shownRef = useRef(false);

	useEffect(() => {
		if (shownRef.current) return;
		shownRef.current = true;
		posthog?.capture("source_blocked_notice_shown", {
			source: sourceName,
			job_id: jobId,
			locale,
		});
	}, [posthog, sourceName, jobId, locale]);

	const title =
		t.sourceBlockedNoticeTitle ||
		"Apply page won't open? Try mobile data or a VPN.";
	const body = (
		t.sourceBlockedNoticeBody ||
		"Some Indonesian ISPs (Telkomsel, Indihome) block {source}. If the page doesn't open after you apply, switch to mobile data."
	).replace("{source}", sourceName);
	const hint =
		t.sourceBlockedNoticeHint || "Still stuck? A VPN usually clears it too.";

	return (
		<div
			role="note"
			data-testid="source-blocked-notice"
			className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-4 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200"
		>
			<ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
			<div className="space-y-1.5">
				<p className="text-sm font-semibold leading-snug">{title}</p>
				<p className="text-xs leading-relaxed">{body}</p>
				<p className="text-xs leading-relaxed text-amber-800/80 dark:text-amber-300/80">
					{hint}
				</p>
			</div>
		</div>
	);
}
